import React, { useReducer, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Stepper,
  Step,
  StepLabel,
  Box,
  Alert,
  AlertTitle,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import FileSelectionStep from './steps/FileSelectionStep'
import ValidationStep from './steps/ValidationStep'
import ProcessingStep from './steps/ProcessingStep'
import ResultsStep from './steps/ResultsStep'

// State management
const INITIAL_STATE = {
  currentStep: 0,
  files: [],
  extractedIdentifiers: [], // e.g., park numbers, chassis numbers, etc.
  validationResult: null,
  uploadProgress: {
    uploaded: 0,
    total: 0,
  },
  processingStatus: null,
  results: null,
  error: null,
}

const Actions = {
  SET_STEP: 'SET_STEP',
  SET_FILES: 'SET_FILES',
  SET_EXTRACTED_IDENTIFIERS: 'SET_EXTRACTED_IDENTIFIERS',
  SET_VALIDATION_RESULT: 'SET_VALIDATION_RESULT',
  UPDATE_UPLOAD_PROGRESS: 'UPDATE_UPLOAD_PROGRESS',
  SET_PROCESSING_STATUS: 'SET_PROCESSING_STATUS',
  SET_RESULTS: 'SET_RESULTS',
  SET_ERROR: 'SET_ERROR',
  RESET: 'RESET',
}

function reducer(state, { action, data }) {
  switch (action) {
    case Actions.SET_STEP:
      return { ...state, currentStep: data }
    case Actions.SET_FILES:
      return { ...state, files: data }
    case Actions.SET_EXTRACTED_IDENTIFIERS:
      return { ...state, extractedIdentifiers: data }
    case Actions.SET_VALIDATION_RESULT:
      return { ...state, validationResult: data }
    case Actions.UPDATE_UPLOAD_PROGRESS:
      return { ...state, uploadProgress: data }
    case Actions.SET_PROCESSING_STATUS:
      return { ...state, processingStatus: data }
    case Actions.SET_RESULTS:
      return { ...state, results: data }
    case Actions.SET_ERROR:
      return { ...state, error: data }
    case Actions.RESET:
      return INITIAL_STATE
    default:
      return state
  }
}

MultiDocumentImportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  // Customization props
  title: PropTypes.string,
  steps: PropTypes.arrayOf(PropTypes.string),
  identifierLabel: PropTypes.string, // e.g., "Park Number", "Chassis Number"
  primaryColor: PropTypes.string, // Custom primary color (e.g., "#1976d2")
  // Delegate props - all business logic is handled by parent
  onExtractIdentifiers: PropTypes.func.isRequired, // Extract identifiers from filenames
  onValidate: PropTypes.func.isRequired, // Validate identifiers
  onUpload: PropTypes.func.isRequired, // Upload files to server
  onStartProcessing: PropTypes.func.isRequired, // Start background processing
  onCheckProgress: PropTypes.func, // Optional: check processing progress
  // Document type handling for unrecognized documents
  documentTypes: PropTypes.array, // List of available document types
  onReassignDocumentType: PropTypes.func, // Reassign document type handler
  onGetPreviewUrl: PropTypes.func, // Get preview URL for a document
  // File picker configuration
  acceptedFileTypes: PropTypes.array,
  maxFiles: PropTypes.number,
  maxFileSize: PropTypes.string,
  // Resume functionality
  resumeBatchId: PropTypes.string, // If provided, opens dialog at results step with this batch
}

export default function MultiDocumentImportDialog({
  open,
  onClose,
  onComplete,
  title,
  steps,
  identifierLabel = 'Identifier',
  primaryColor,
  onExtractIdentifiers,
  onValidate,
  onUpload,
  onStartProcessing,
  onCheckProgress,
  documentTypes = [],
  onReassignDocumentType,
  onGetPreviewUrl,
  acceptedFileTypes = ['application/pdf'],
  maxFiles = 50,
  maxFileSize = '100MB',
  resumeBatchId,
}) {
  const { t } = useTranslation()
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE)
  const unassignedUnrecognizedCountRef = React.useRef(0)

  // Create custom theme if primaryColor is provided
  const customTheme = primaryColor
    ? createTheme({
        palette: {
          primary: {
            main: primaryColor,
          },
        },
      })
    : null

  const defaultTitle =
    title ||
    t(
      'components.multi-document-import-dialog.title',
      'Import Multiple Documents'
    )
  const defaultSteps = steps || [
    t('components.multi-document-import-dialog.step-select', 'Select Files'),
    t('components.multi-document-import-dialog.step-validate', 'Validate'),
    t('components.multi-document-import-dialog.step-upload', 'Upload'),
    t('components.multi-document-import-dialog.step-results', 'Results'),
  ]

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      dispatch({ action: Actions.RESET })
    }
  }, [open])

  // Resume at results step if resumeBatchId is provided
  useEffect(() => {
    if (open && resumeBatchId && onCheckProgress) {
      console.log(
        '[MultiDocumentImportDialog] Resuming with batch ID:',
        resumeBatchId
      )

      // Set the step to results (step 3)
      dispatch({ action: Actions.SET_STEP, data: 3 })

      // Set the processing status with the batch ID
      dispatch({
        action: Actions.SET_PROCESSING_STATUS,
        data: { batchId: resumeBatchId },
      })

      // Load the batch status
      const loadBatchStatus = async () => {
        try {
          const progressData = await onCheckProgress(resumeBatchId)
          dispatch({ action: Actions.SET_RESULTS, data: progressData })
        } catch (error) {
          console.error(
            '[MultiDocumentImportDialog] Error loading batch status:',
            error
          )
          dispatch({ action: Actions.SET_ERROR, data: error.message })
        }
      }

      loadBatchStatus()
    }
  }, [open, resumeBatchId, onCheckProgress])

  // Handlers for each step
  const handleFilesSelected = async (files) => {
    dispatch({ action: Actions.SET_FILES, data: files })

    try {
      // Extract identifiers from filenames (e.g., park numbers)
      const identifiers = await onExtractIdentifiers(files)
      dispatch({ action: Actions.SET_EXTRACTED_IDENTIFIERS, data: identifiers })
    } catch (error) {
      dispatch({ action: Actions.SET_ERROR, data: error.message })
    }
  }

  const handleValidate = async () => {
    dispatch({ action: Actions.SET_ERROR, data: null })

    try {
      const validationResult = await onValidate(state.extractedIdentifiers)
      dispatch({
        action: Actions.SET_VALIDATION_RESULT,
        data: validationResult,
      })

      // Always move to validation step to show results (valid or invalid)
      dispatch({ action: Actions.SET_STEP, data: state.currentStep + 1 })
    } catch (error) {
      dispatch({ action: Actions.SET_ERROR, data: error.message })
    }
  }

  const handleUpload = async () => {
    dispatch({ action: Actions.SET_ERROR, data: null })

    try {
      // Filter files to only upload valid ones
      const validFiles = state.files.filter((file, index) => {
        const identifier = state.extractedIdentifiers[index]
        return state.validationResult.valid.includes(identifier?.value)
      })

      dispatch({
        action: Actions.UPDATE_UPLOAD_PROGRESS,
        data: { uploaded: 0, total: validFiles.length },
      })

      // Upload files with progress tracking
      const uploadResults = await onUpload(validFiles, (progress) => {
        dispatch({
          action: Actions.UPDATE_UPLOAD_PROGRESS,
          data: progress,
        })
      })

      // Start background processing
      const processingResult = await onStartProcessing(uploadResults)
      dispatch({
        action: Actions.SET_PROCESSING_STATUS,
        data: processingResult,
      })

      // Move to results step
      dispatch({ action: Actions.SET_STEP, data: state.currentStep + 1 })
    } catch (error) {
      dispatch({ action: Actions.SET_ERROR, data: error.message })
    }
  }

  const handleCheckProgress = async () => {
    if (!onCheckProgress || !state.processingStatus) return

    try {
      const progressData = await onCheckProgress(state.processingStatus.batchId)
      dispatch({ action: Actions.SET_RESULTS, data: progressData })

      // If processing is complete or failed, move to results step
      if (
        progressData &&
        (progressData.status === 'completed' ||
          progressData.status === 'failed')
      ) {
        // Only transition if we're still on the processing step
        if (state.currentStep === 2) {
          dispatch({ action: Actions.SET_STEP, data: 3 })
        }
      }
    } catch (error) {
      console.error('Error checking progress:', error)
    }
  }

  const handleClose = () => {
    if (state.currentStep === defaultSteps.length - 1 && state.results) {
      // If on results step with successful results, call onComplete
      if (onComplete) {
        // Include unassigned unrecognized documents count in results
        const enhancedResults = {
          ...state.results,
          unassignedUnrecognizedCount: unassignedUnrecognizedCountRef.current,
        }
        onComplete(enhancedResults)
      }
    }
    onClose()
  }

  const handleStepClick = (stepIndex) => {
    // Allow going back to previous steps
    if (stepIndex < state.currentStep) {
      dispatch({ action: Actions.SET_STEP, data: stepIndex })
    }
  }

  const renderStepContent = () => {
    switch (state.currentStep) {
      case 0:
        return (
          <FileSelectionStep
            files={state.files}
            extractedIdentifiers={state.extractedIdentifiers}
            identifierLabel={identifierLabel}
            onFilesChange={handleFilesSelected}
            onNext={handleValidate}
            acceptedFileTypes={acceptedFileTypes}
            maxFiles={maxFiles}
            maxFileSize={maxFileSize}
          />
        )
      case 1:
        return (
          <ValidationStep
            identifiers={state.extractedIdentifiers}
            validationResult={state.validationResult}
            identifierLabel={identifierLabel}
            onBack={() => dispatch({ action: Actions.SET_STEP, data: 0 })}
            onNext={handleUpload}
          />
        )
      case 2:
        return (
          <ProcessingStep
            uploadProgress={state.uploadProgress}
            processingStatus={state.processingStatus}
            onCheckProgress={handleCheckProgress}
          />
        )
      case 3:
        return (
          <ResultsStep
            results={state.results}
            processingStatus={state.processingStatus}
            onCheckProgress={handleCheckProgress}
            onClose={handleClose}
            documentTypes={documentTypes}
            onReassignDocumentType={onReassignDocumentType}
            onGetPreviewUrl={onGetPreviewUrl}
            onUnassignedCountChange={(count) => {
              unassignedUnrecognizedCountRef.current = count
            }}
          />
        )
      default:
        return null
    }
  }

  const dialogContent = (
    <>
      <Box sx={{ width: '100%', mt: 3, mb: 3 }}>
        <Stepper activeStep={state.currentStep}>
          {defaultSteps.map((label, index) => (
            <Step
              key={label}
              onClick={() => handleStepClick(index)}
              sx={{ cursor: index < state.currentStep ? 'pointer' : 'default' }}
            >
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {state.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{t('common.error', 'Error')}</AlertTitle>
          {state.error}
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>{renderStepContent()}</Box>
    </>
  )

  return (
    <Dialog
      open={open}
      onClose={() => handleClose?.()}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle>{defaultTitle}</DialogTitle>
      <DialogContent dividers>
        {customTheme ? (
          <ThemeProvider theme={customTheme}>{dialogContent}</ThemeProvider>
        ) : (
          dialogContent
        )}
      </DialogContent>
    </Dialog>
  )
}
